import {getFullPathString, getUniqueId, getUrl, logoUrlSecurityCheck} from './utils/utils';
import {changeElementAttributes, createElement, removeElement, renderElement} from './utils/domManager';
import messageClient from './messageClient';
import overlay from './overlay';
import fullScreenButtonComponent from './components/fullScreenButton';
import studioLinkButtonComponent from './components/studioLinkButton';
import messageServer from './messageServer';

const defaultOptions = {
    width: '100%',
    height: '100%',
    frameBorder: '0',
    scrolling: 'no'
};

/**
 * Iframe container module
 * @param {URL} hostUrl url host
 * @param {string} urlPath path
 * @param {Object} options
 * @returns {Object}
 */
const iframe = (hostUrl, urlPath, options = {}, accessToken) => {
    const attributes = Object.assign({}, defaultOptions, { id: getUniqueId() });
    const iframeDomElement = createElement('iframe', attributes);
    const overlayLayer = options.interactivity.afterClick ? createOverlay() : null;
    const messageClientInstance = messageClient(iframeDomElement, hostUrl);
    const messageServerInstance = messageServer(hostUrl, getId());
    const message = {
        ...messageClientInstance,
        ...messageServerInstance,
        setEmbeddingSettings: () => messageClientInstance.setEmbeddingSettings(options.componentSettings)
    };
    const fullScreenButton = options.fullScreenButton.enabled ? createFullScreenButton() : null;
    const studioLinkButton = options.studioLinkButton.enabled ? createStudioLinkButton() : null;

    setState(urlPath);

    /**
     * Sets iframe content state. May be asynchronous.
     * @param {string} urlPath path
     * @returns {boolean}
     */
    function setState(urlPath) {
        let urlParams = logoUrlSecurityCheck(options.theme.logoSrc) ? {'logo_url': options.theme.logoSrc} : {};
        if (options.theme.colorPrimary) {
            urlParams = Object.assign({}, urlParams, {'color_primary': options.theme.colorPrimary});
        }
        if (options.componentSettings?.infoBox?.defaultExpanded !== undefined) {
            urlParams = Object.assign({},
                urlParams,
                {'infobox_default_expanded': options.componentSettings.infoBox.defaultExpanded ? 1 : 0}
            );
        }
        if (options.componentSettings?.legend?.defaultExpanded !== undefined) {
            urlParams = Object.assign({},
                urlParams,
                {'legend_default_expanded': options.componentSettings.legend.defaultExpanded ? 1 : 0}
            );
        }
        if (accessToken) {
            urlParams = Object.assign({}, urlParams, {'access_token': accessToken});
        }
        const fullPathString = getFullPathString(urlPath, urlParams);
        const src = getUrl(hostUrl, fullPathString).toString();
        if (attributes.src === src) {
            // Hacky way to refresh iframe to desired state even if src is the same
            attributes.src = '';
            changeElementAttributes(iframeDomElement, attributes);
            setTimeout(() => {
                attributes.src = src;
                changeElementAttributes(iframeDomElement, attributes);
            }, 50);
        } else {
            attributes.src = src;
            changeElementAttributes(iframeDomElement, attributes);
        }

        return true;
    }

    /**
     * Renders iframe into the element as a child
     * @param {string|Object} containerElement dom element or element id
     * @returns {boolean}
     */
    function render(containerElement) {
        renderElement(containerElement, iframeDomElement);
        if (overlayLayer) {
            overlayLayer.render(containerElement);
        }
        if (fullScreenButton) {
            fullScreenButton.render(containerElement);
        }
        if (studioLinkButton) {
            studioLinkButton.render(containerElement);
        }
        return true;
    }

    /**
     * Removes iframe from the element
     * @param {string|Object} containerElement dom element or element id
     * @returns {boolean}
     */
    function remove(containerElement) {
        removeElement(containerElement, iframeDomElement);
        if (overlayLayer) {
            overlayLayer.remove(containerElement);
        }
        if (fullScreenButton) {
            fullScreenButton.remove(containerElement);
        }
        if (studioLinkButton) {
            studioLinkButton.remove(containerElement);
        }

        return true;
    }

    /**
     * Returns iframe unique id
     * @returns {string}
     */
    function getId() {
        return attributes.id;
    }

    /**
     * Factory for creating overlay objects
     * @returns {Object} overlay object
     */
    function createOverlay() {
        return overlay(options);
    }

    /**
     * Factory for creating full screen button
     * @returns {Object} fullScreenButton object
     */
    function createFullScreenButton() {
        return fullScreenButtonComponent(options);
    }

    /**
     * Factory for creating Studio link button
     * @returns {Object} studioLinkButton object
     */
    function createStudioLinkButton() {
        return studioLinkButtonComponent(`${hostUrl.href}#${urlPath}`, options);
    }

    return {
        setState,
        render,
        remove,
        getId,
        message
    };
};

export default iframe;
